import type React from "react"

import { Box, Stack, Typography } from "@mui/material"

import type { EnduseDelta } from "./disaggregation.types"
import { enduseColor, enduseLabel } from "./enduse-colors"

const R = 70
const STROKE = 22
const C = 2 * Math.PI * R
const OTHER_COLOR = "#cbd5e1"

/**
 * Donut of end-use share (kWh). Sub-1% slices collapse into an "Other" entry.
 *
 * `@/formatters/use-format-number` doesn't exist in this codebase, so the
 * total is formatted with a plain `toLocaleString()` (same fallback the
 * plan calls out).
 */
export const EnduseDonut: React.FC<{
  enduses: EnduseDelta[]
  totalKwh: number
}> = ({ enduses, totalKwh }) => {
  const major = enduses.filter((e) => e.sharePct >= 1)
  const otherShare = enduses
    .filter((e) => e.sharePct < 1)
    .reduce((s, e) => s + e.sharePct, 0)
  const segments = [
    ...major.map((e) => ({
      key: e.enduse,
      label: enduseLabel(e.enduse),
      share: e.sharePct,
      color: enduseColor(e.enduse),
    })),
    ...(otherShare > 0
      ? [
          {
            key: "__other",
            label: "Other",
            share: otherShare,
            color: OTHER_COLOR,
          },
        ]
      : []),
  ]

  let offset = 0
  return (
    <Stack direction="row" spacing={2.5} sx={{ alignItems: "center" }}>
      <Box
        sx={{
          position: "relative",
          width: 2 * (R + STROKE / 2),
          height: 2 * (R + STROKE / 2),
        }}
      >
        <svg
          height="100%"
          viewBox={`0 0 ${2 * (R + STROKE / 2)} ${2 * (R + STROKE / 2)}`}
          width="100%"
        >
          <g
            transform={`translate(${R + STROKE / 2}, ${R + STROKE / 2}) rotate(-90)`}
          >
            {segments.map((s) => {
              const len = (s.share / 100) * C
              const dash = `${len} ${C - len}`
              const el = (
                <circle
                  cx={0}
                  cy={0}
                  data-testid="donut-segment"
                  fill="none"
                  key={s.key}
                  r={R}
                  stroke={s.color}
                  strokeDasharray={dash}
                  strokeDashoffset={-offset}
                  strokeWidth={STROKE}
                />
              )
              offset += len
              return el
            })}
          </g>
        </svg>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontWeight: "bold", lineHeight: 1 }} variant="h3">
            {Math.round(totalKwh).toLocaleString()}
          </Typography>
          <Typography color="text.secondary" variant="caption">
            kWh
          </Typography>
        </Box>
      </Box>
      <Stack spacing={0.5} sx={{ flex: 1 }}>
        {segments.map((s) => (
          <Stack
            direction="row"
            key={s.key}
            spacing={1}
            sx={{ alignItems: "center" }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                bgcolor: s.color,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ flex: 1 }} variant="dataLabel">
              {s.label}
            </Typography>
            <Typography
              sx={{ fontVariantNumeric: "tabular-nums" }}
              variant="dataLabel"
            >
              {Math.round(s.share)}%
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}
